import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import ps from './Pallets.module.css'
import styles from './StockSettings.module.css'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { OrderModal } from './OrderModal'
import { CloseButton } from './SdPanel'
import { approvalChip } from './orders'

// Configuration → Stock & Dispatch → Customers (V4 EX-03's Customers tab and
// customer drawer; handoff §7n.7). The list, and a drawer per customer with
// its contacts, destinations, label requirement and notes, and every order it
// has. Every rule is the API's (routes/customers.js): this screen shows what
// it returns and says what it refused.
//
// V4 has no forms for a customer or a destination; the ones here are mine
// (CUST-1, provisional). The Xero link tab is shown with the pricing
// permission only, and capabilities (#22) do not exist yet, so it is not
// shown. Label templates (§7n.8) are not built, so none can match.

export type CustomerKind = 'local' | 'export'

export interface Customer {
  id: string
  name: string
  kind: CustomerKind | null
  contacts: string | null
  label_requirement: string | null
  notes: string | null
  archived_at: string | null
  destinations: number
  destinations_archived: number
  orders: number
  archived: boolean
  state: 'active' | 'archived'
  removable: boolean
}

export interface Destination {
  id: string
  name: string
  address: string | null
  country: string | null
  port: string | null
  importer_consignee: string | null
  delivery_instructions: string | null
  archived: boolean
  archived_at: string | null
  orders: number
  state: 'active' | 'archived'
  removable: boolean
}

export interface CustomerOrder {
  id: string
  order_reference: string
  requested_dispatch_date: string | null
  destination_name: string | null
  approval_status: string
  approval_label: string
  ordered: number
  dispatched: number
  complete: boolean
}

export interface CustomerRecord extends Customer {
  destination_list: Destination[]
  order_list: CustomerOrder[]
}

type DrawerTab = 'details' | 'dests' | 'labels' | 'notes'

const DRAWER_TABS: [DrawerTab, string][] = [
  ['details', 'Customer & contacts'],
  ['dests', 'Destinations'],
  ['labels', 'Label requirements'],
  ['notes', 'Notes & orders'],
]

// V4 CU_HELP, verbatim, less the Receiving establishment card: §7n.6 puts
// the receiving establishment on the order and V4 on the destination, and
// neither is built (CUST-3). The Xero tab's help goes with the tab.
const DRAWER_HELP: Record<DrawerTab, { title: string; parts: [string, string][] }> = {
  details: {
    title: 'What this tab holds',
    parts: [
      ['The company, not the delivery', 'Name, local or export, and the people to contact. Where the goods go is the next tab. A customer with one destination still has both.'],
      ['Local or export decides the paperwork', 'An export customer gains freight references on its orders (airway bill, flight, container, forwarder, receiving establishment) and matches the export label templates.'],
      ['Archive rather than delete', 'Archiving stops new orders being raised and keeps every historical order, pallet and invoice readable.'],
    ],
  },
  dests: {
    title: 'How destinations work',
    parts: [
      ['An order names one destination', 'Address, port, consignee and delivery instructions live here, so a detail wrong on this tab is wrong on every docket that follows.'],
      ['At least one, often several', 'A customer with two DCs has two destinations; they are chosen per order, not per customer.'],
    ],
  },
  labels: {
    title: 'What label requirements mean',
    parts: [
      ['What this customer expects on the carton', 'The stated requirement in plain words. What they have asked for, not what the system prints.'],
      ['The templates that can match', 'Below it, every Dataphyll template whose rule could select this customer, by transport mode. Exactly one should match per mode.'],
      ['Zero or two matches is a problem', 'Where none matches or more than one does, printing stops and asks for an explicit choice, and records who chose. Fix the rule rather than choosing every time.'],
    ],
  },
  notes: {
    title: 'Notes and order history',
    parts: [
      ['Notes are internal', 'Nothing here prints on a document or reaches the customer. Delivery instructions that must travel with the goods belong on the destination.'],
      ['Every order, not just the open ones', 'The list is the customer’s full history, so a dispute can be traced back without leaving the record.'],
    ],
  },
}

export const kindLabel = (k: CustomerKind | null) => (k === 'export' ? 'Export' : k === 'local' ? 'Local' : 'Not classified')

const errText = (e: any) => e?.body?.error || e?.message || String(e)
const errList = (e: any): string[] => (Array.isArray(e?.body?.errors) && e.body.errors.length ? e.body.errors : [errText(e)])
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`
const dash = (v: string | null | undefined) => (v && v.trim() ? v : '—')

// The day as V4 shows it (fmtD): "30 Sep 2026".
function fmtDay(d: string | null) {
  if (!d) return 'Undated'
  const [y, m, day] = d.split('-').map(Number)
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]
  return `${day} ${mon} ${y}`
}

const StateChip = ({ archived }: { archived: boolean }) => (
  <span className={archived ? styles.chipArchived : styles.chipOk}>{archived ? 'Archived' : 'Active'}</span>
)

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export function CustomersTab({ user, openId, onOpen }: { user: any; openId: string | null; onOpen: (id: string | null) => void }) {
  const [items, setItems] = useState<Customer[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [adding, setAdding] = useState(false)
  const [toast, setToast] = useState('')
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid

  const load = useCallback(async () => {
    if (!userRef.current) return
    try {
      setItems((await apiJson<{ items: Customer[] }>(userRef.current, '/api/settings/customers')).items)
      setLoadError('')
    } catch (e) {
      setLoadError(`Failed to load customers: ${errText(e)}`)
    }
  }, [])

  useEffect(() => {
    if (uid) load()
  }, [uid, load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const live = (items || []).filter((c) => !c.archived).length

  return (
    <>
      <div className={styles.cuTop}>
        {items && (
          <span className={styles.note} data-testid="customer-count">
            {live} active · {(items || []).length - live} archived
          </span>
        )}
        <button type="button" className={pa.primaryBtn} style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)} disabled={adding}>
          New customer
        </button>
      </div>
      {loadError && (
        <div className={styles.error} role="alert">
          {loadError}
        </div>
      )}
      {adding && (
        <CustomerForm
          user={user}
          onCancel={() => setAdding(false)}
          onSaved={(r) => {
            setAdding(false)
            setToast(`Added ${r.name}`)
            load()
            onOpen(r.id)
          }}
        />
      )}
      <div className={styles.listCard}>
        <div className={styles.cuHead}>
          <span>Customer</span>
          <span>Type</span>
          <span>Contacts</span>
          <span>Destinations</span>
          <span>State</span>
        </div>
        {items && items.length === 0 && <div className={styles.emptyRow}>No customers yet.</div>}
        {!items && !loadError && <div className={styles.emptyRow}>Loading…</div>}
        {(items || []).map((c) => (
          <button key={c.id} type="button" className={styles.cuRow} data-testid="customer-row" onClick={() => onOpen(c.id)}>
            <span className={c.archived ? styles.nameArchived : styles.cuName}>{c.name}</span>
            <span className={c.kind ? undefined : styles.muted}>{kindLabel(c.kind)}</span>
            <span className={styles.cuContacts}>{dash(c.contacts)}</span>
            <span className={c.destinations ? undefined : styles.warnText}>{plural(c.destinations, 'destination')}</span>
            <span>
              <StateChip archived={c.archived} />
            </span>
          </button>
        ))}
      </div>
      {openId && (
        <CustomerDrawer
          user={user}
          customerId={openId}
          onClose={() => onOpen(null)}
          onChanged={(msg) => {
            if (msg) setToast(msg)
            load()
          }}
        />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

function Errors({ list }: { list: string[] }) {
  if (!list.length) return null
  return (
    <div className={ps.errList} role="alert">
      {list.map((e) => (
        <div key={e}>{e}</div>
      ))}
    </div>
  )
}

// New customer, and Edit on the Customer & contacts tab. In the drawer
// (`layered`) it is a guarded layer (h 425-458): Escape cancels an untouched
// form silently and asks on a changed one. On the list (New customer) it is
// an inline page form, not an overlay, and is left as it was.
function CustomerForm({
  user,
  current,
  layered = false,
  onCancel,
  onSaved,
}: {
  user: any
  current?: CustomerRecord
  layered?: boolean
  onCancel: () => void
  onSaved: (r: CustomerRecord) => void
}) {
  const [name, setName] = useState(current?.name || '')
  const [kind, setKind] = useState<CustomerKind | null>(current?.kind || null)
  const [contacts, setContacts] = useState(current?.contacts || '')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  const formEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ name, kind, contacts })
  // h 3984-3992: busy prevents close, Cancel disabled while busy.
  const { requestClose, confirm } = useLayer(layered, onCancel, { dirty: layered && dirty && !busy, el: formEl, busy })

  const save = async () => {
    setBusy(true)
    setErrors([])
    try {
      const body: Record<string, unknown> = { name, contacts }
      if (kind) body.kind = kind
      const r = current
        ? await apiJson<CustomerRecord>(user, `/api/settings/customers/${current.id}`, { method: 'PUT', body })
        : await apiJson<CustomerRecord>(user, '/api/settings/customers', { method: 'POST', body })
      onSaved(r)
    } catch (e) {
      setErrors(errList(e))
    } finally {
      setBusy(false)
    }
  }

  const kindChanged = !!current?.kind && kind !== current.kind

  return (
    <div ref={formEl} className={styles.formCard} data-testid="customer-form">
      <div className={styles.formTitle}>{current ? 'Edit customer' : 'New customer'}</div>
      <div className={inertClass(busy, slow)}>
        <label className={styles.formField}>
          <span className={ps.fieldLabel}>Name</span>
          <input className={ps.input} value={name} onChange={(e) => setName(e.target.value)} aria-label="Customer name" autoFocus />
        </label>
        <div className={styles.formField}>
          <span className={ps.fieldLabel}>Local or export</span>
          <div className={styles.seg} role="radiogroup" aria-label="Local or export">
            {(['local', 'export'] as CustomerKind[]).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? styles.segOn : styles.segBtn} onClick={() => setKind(k)}>
                {kindLabel(k)}
              </button>
            ))}
          </div>
          {kindChanged && <span className={ps.note}>Orders already raised keep local / export as saved; an order takes the new one the next time it is edited.</span>}
        </div>
        <label className={styles.formField}>
          <span className={ps.fieldLabel}>Contacts</span>
          <textarea className={styles.textarea} value={contacts} onChange={(e) => setContacts(e.target.value)} aria-label="Contacts" placeholder="Who to contact, and how" />
        </label>
        <Errors list={errors} />
      </div>
      <div className={ps.actions}>
        <PendingButton className={pa.primaryBtn} busy={busy} pending={current ? 'Saving…' : 'Creating…'} onClick={save}>
          {current ? 'Save' : 'Create customer'}
        </PendingButton>
        <button type="button" className={pa.ghostBtn} disabled={busy} onClick={requestClose}>
          Cancel
        </button>
      </div>
      {confirm}
    </div>
  )
}

const DEST_FIELDS: [keyof Destination, string, boolean][] = [
  ['name', 'Name', false],
  ['address', 'Address', true],
  ['country', 'Country', false],
  ['port', 'Port', false],
  ['importer_consignee', 'Importer / consignee', true],
  ['delivery_instructions', 'Delivery instructions', true],
]

function DestinationForm({
  user,
  customerId,
  current,
  onCancel,
  onSaved,
}: {
  user: any
  customerId: string
  current?: Destination
  onCancel: () => void
  onSaved: (r: CustomerRecord) => void
}) {
  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(DEST_FIELDS.map(([k]) => [k, ((current?.[k] as string | null) || '') as string]))
  )
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  // A guarded layer in the drawer (h 425-458); its first field takes focus.
  // h 3984-3992: busy prevents close, Cancel disabled while busy.
  const formEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(form)
  const { requestClose, confirm } = useLayer(true, onCancel, { dirty: dirty && !busy, el: formEl, focusFirst: true, busy })

  const save = async () => {
    setBusy(true)
    setErrors([])
    try {
      const base = `/api/settings/customers/${customerId}/destinations`
      const r = current
        ? await apiJson<CustomerRecord>(user, `${base}/${current.id}`, { method: 'PUT', body: form })
        : await apiJson<CustomerRecord>(user, base, { method: 'POST', body: form })
      onSaved(r)
    } catch (e) {
      setErrors(errList(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div ref={formEl} className={styles.formCard} data-testid="destination-form">
      <div className={styles.formTitle}>{current ? `Edit ${current.name}` : 'New destination'}</div>
      <div className={inertClass(busy, slow)}>
        <div className={ps.twoCol}>
          {DEST_FIELDS.map(([k, label, long]) => (
            <label key={k} className={long ? `${styles.formField} ${ps.full}` : styles.formField}>
              <span className={ps.fieldLabel}>{label}</span>
              {long ? (
                <textarea className={styles.textarea} aria-label={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              ) : (
                <input className={ps.input} aria-label={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
            </label>
          ))}
        </div>
        {current && current.orders > 0 && (
          <span className={ps.note}>
            {plural(current.orders, 'order')} name{current.orders === 1 ? 's' : ''} this destination and will show what you save here.
          </span>
        )}
        <Errors list={errors} />
      </div>
      <div className={ps.actions}>
        <PendingButton className={pa.primaryBtn} busy={busy} pending={current ? 'Saving…' : 'Adding…'} onClick={save}>
          {current ? 'Save' : 'Add destination'}
        </PendingButton>
        <button type="button" className={pa.ghostBtn} disabled={busy} onClick={requestClose}>
          Cancel
        </button>
      </div>
      {confirm}
    </div>
  )
}

// One free-text field of the customer: the label requirement, or the notes.
function TextField({
  user,
  rec,
  field,
  label,
  empty,
  onSaved,
}: {
  user: any
  rec: CustomerRecord
  field: 'label_requirement' | 'notes'
  label: string
  empty: string
  onSaved: (r: CustomerRecord) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(rec[field] || '')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  // While editing it is a guarded layer (h 425-458): changed from the saved
  // text, Escape and Cancel ask first. h 3984-3992: busy prevents close,
  // Cancel disabled while busy.
  const editEl = useRef<HTMLDivElement>(null)
  const dirty = editing && value !== (rec[field] || '') && !busy
  const { requestClose, confirm } = useLayer(editing, () => setEditing(false), { dirty, el: editEl, busy })

  const save = async () => {
    setBusy(true)
    setErrors([])
    try {
      const r = await apiJson<CustomerRecord>(user, `/api/settings/customers/${rec.id}`, { method: 'PUT', body: { [field]: value } })
      setEditing(false)
      onSaved(r)
    } catch (e) {
      setErrors(errList(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div ref={editEl} className={styles.infoCard}>
      <div className={styles.infoHead}>
        <span className={ps.section} style={{ marginTop: 0 }}>
          {label}
        </span>
        {!editing && (
          <button
            type="button"
            className={styles.rowBtn}
            onClick={() => {
              setValue(rec[field] || '')
              setEditing(true)
            }}
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <div className={inertClass(busy, slow)}>
            <textarea className={styles.textarea} aria-label={label} value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
            <Errors list={errors} />
          </div>
          <div className={ps.actions}>
            <PendingButton className={pa.primaryBtn} busy={busy} pending="Saving…" onClick={save}>
              Save
            </PendingButton>
            <button type="button" className={pa.ghostBtn} disabled={busy} onClick={requestClose}>
              Cancel
            </button>
          </div>
          {confirm}
        </>
      ) : (
        <div className={rec[field] ? styles.infoText : styles.infoEmpty}>{rec[field] || empty}</div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The drawer
// ---------------------------------------------------------------------------

export function CustomerDrawer({
  user,
  customerId,
  onClose,
  onChanged,
}: {
  user: any
  customerId: string
  onClose: () => void
  // a message is a toast; none means only "refetch the list"
  onChanged: (message?: string) => void
}) {
  const [rec, setRec] = useState<CustomerRecord | null>(null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<DrawerTab>('details')
  const [editing, setEditing] = useState(false)
  const [destForm, setDestForm] = useState<null | 'new' | string>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)
  // The write in flight: 'archive', 'remove', or 'archive:<id>' / 'remove:<id>'
  // for a destination row, so only the pressed button says what it is doing.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const slow = useSlow(busy)
  const [helpOn, toggleHelp] = useHelpPref(user, 'customer_record')

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      setRec(await apiJson<CustomerRecord>(userRef.current, `/api/settings/customers/${customerId}`))
      setError('')
    } catch (e) {
      setError(`Failed to load the customer: ${errText(e)}`)
    }
  }, [customerId])

  useEffect(() => {
    setRec(null)
    setTab('details')
    setEditing(false)
    setDestForm(null)
    setConfirmRemove(false)
    load()
  }, [load])

  // Escape closes the topmost layer only (h 425-458). The drawer is a layer;
  // the remove confirm, each inline form and an order opened from the history
  // register their own, so the stack peels them one press at a time.
  const drawerEl = useRef<HTMLDivElement>(null)
  const drawer = useLayer(true, onClose, { el: drawerEl, modal: true, busy })
  const removePanel = useLayer(confirmRemove && !!rec, () => setConfirmRemove(false), { busy })
  // The drawer's ways out of an open form that are not the form's own close
  // (another tab, another destination's Edit, Add destination) ask first when
  // it holds changes: the drawer's guard asks for a layer over it. The scrim
  // and the × do the same through the drawer's own requestClose.
  const leaveForm = drawer.guard

  const saved = (r: CustomerRecord, message?: string) => {
    setRec(r)
    setError('')
    onChanged(message)
  }

  const act = async (on: string, path: string, method: 'POST' | 'DELETE', message: (r: any) => string) => {
    setBusyOn(on)
    setError('')
    try {
      const r = await apiJson<any>(userRef.current, `/api/settings/customers/${customerId}${path}`, { method })
      return { r, message: message(r) }
    } catch (e) {
      setError(errText(e))
      await load()
      return null
    } finally {
      setBusyOn(null)
    }
  }

  const archiveToggle = async () => {
    if (!rec) return
    const move = rec.archived ? 'restore' : 'archive'
    const out = await act('archive', `/${move}`, 'POST', () =>
      rec.archived ? `${rec.name} restored` : `${rec.name} archived. Historical orders keep their saved details`
    )
    if (out) saved(out.r, out.message)
  }

  const removeCustomer = async () => {
    if (!rec) return
    const out = await act('remove', '', 'DELETE', () => `Removed ${rec.name}`)
    if (out) {
      onChanged(out.message)
      onClose()
    }
  }

  const destAct = async (d: Destination, move: 'archive' | 'restore' | 'remove') => {
    const out = await act(
      `${move === 'remove' ? 'remove' : 'archive'}:${d.id}`,
      move === 'remove' ? `/destinations/${d.id}` : `/destinations/${d.id}/${move}`,
      move === 'remove' ? 'DELETE' : 'POST',
      () =>
        move === 'remove'
          ? `Removed ${d.name}`
          : move === 'archive'
            ? `${d.name} archived. Orders that name it keep it; new orders cannot choose it`
            : `${d.name} restored`
    )
    if (out) saved(out.r, out.message)
  }

  const help = DRAWER_HELP[tab]
  const liveDests = rec ? rec.destination_list.filter((d) => !d.archived).length : 0

  return (
    <>
      <div className={styles.scrim} onClick={drawer.requestClose} />
      <div ref={drawerEl} className={styles.drawer} role="dialog" aria-label={rec ? `Customer ${rec.name}` : 'Customer'}>
        <div className={styles.drawerHead}>
          <div className={styles.drawerTitleRow}>
            <span className={styles.drawerTitle}>{rec?.name || 'Customer'}</span>
            {rec && <StateChip archived={rec.archived} />}
            {rec && <span className={styles.drawerKind}>{rec.kind ? `${kindLabel(rec.kind)} customer` : 'Not classified'}</span>}
            <div className={ps.dHeadRight}>
              {rec?.removable && (
                <button type="button" className={ps.dangerBtn} disabled={busy} onClick={() => setConfirmRemove(true)}>
                  Remove
                </button>
              )}
              {rec && (
                <PendingButton
                  className={pa.ghostBtn}
                  busy={busyOn === 'archive'}
                  pending={rec.archived ? 'Restoring…' : 'Archiving…'}
                  disabled={busy}
                  onClick={archiveToggle}
                >
                  {rec.archived ? 'Restore customer' : 'Archive customer'}
                </PendingButton>
              )}
              <CloseButton title="Close: the customer list stays behind" disabled={busy} onClick={drawer.requestClose} />
            </div>
          </div>
          {error && (
            <div className={ps.alert} role="alert">
              {error}
            </div>
          )}
          {confirmRemove && rec && (
            <div className={styles.confirm} data-testid="remove-panel">
              <div className={ps.strong}>Remove {rec.name}?</div>
              <div className={ps.sub}>
                No order names it, so it can go
                {rec.destination_list.length ? `, with its ${plural(rec.destination_list.length, 'destination')}` : ''}. This cannot be undone; archive it
                instead to keep it.
              </div>
              <div className={ps.actions}>
                <PendingButton className={ps.dangerFill} busy={busyOn === 'remove'} pending="Removing…" disabled={busy} onClick={removeCustomer}>
                  Remove customer
                </PendingButton>
                <button type="button" className={pa.ghostBtn} disabled={busy} onClick={removePanel.requestClose}>
                  Keep it
                </button>
              </div>
              {removePanel.confirm}
            </div>
          )}
          <div className={styles.drawerTabs}>
            <div className={pa.tabTrack} role="tablist" aria-label="Customer record">
              {DRAWER_TABS.map(([k, t]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? pa.tabOn : pa.tab} onClick={() => (k === tab ? undefined : leaveForm(() => setTab(k)))}>
                  {t}
                </button>
              ))}
            </div>
            <button type="button" className={pa.helpBtn} style={{ marginLeft: 'auto' }} onClick={toggleHelp}>
              {helpOn ? 'Hide help' : 'How this tab works'}
            </button>
          </div>
        </div>

        <div className={ps.dBody}>
          {helpOn && (
            <div className={ps.helpBox} data-testid="customer-help">
              <button type="button" className={ps.helpX} title="Hide help" aria-label="Hide help" onClick={toggleHelp}>
                ×
              </button>
              <div className={ps.strong}>{help.title}</div>
              {help.parts.map(([k, v]) => (
                <div key={k} className={ps.helpItem}>
                  <span>{k}</span>
                  <p>{v}</p>
                </div>
              ))}
            </div>
          )}

          {rec && tab === 'details' &&
            (editing ? (
              <CustomerForm
                user={user}
                current={rec}
                layered
                onCancel={() => setEditing(false)}
                onSaved={(r) => {
                  setEditing(false)
                  saved(r, `Saved ${r.name}`)
                }}
              />
            ) : (
              <div className={styles.infoCard}>
                <div className={styles.infoHead}>
                  <span className={ps.section} style={{ marginTop: 0 }}>
                    Customer &amp; contacts
                  </span>
                  <button type="button" className={styles.rowBtn} onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>
                <div className={ps.facts}>
                  <div className={ps.fact}>
                    <span className={ps.factK}>Name</span>
                    <span>{rec.name}</span>
                  </div>
                  <div className={ps.fact}>
                    <span className={ps.factK}>Local or export</span>
                    <span>{rec.kind ? kindLabel(rec.kind) : 'Not classified: its orders are classified one by one until you choose'}</span>
                  </div>
                  <div className={ps.fact}>
                    <span className={ps.factK}>Contacts</span>
                    <span className={styles.infoText}>{dash(rec.contacts)}</span>
                  </div>
                </div>
              </div>
            ))}

          {rec && tab === 'dests' && (
            <>
              {liveDests === 0 && <div className={ps.amber}>No live destination: an order needs one, so none can be raised for {rec.name}.</div>}
              {rec.destination_list.map((d) =>
                destForm === d.id ? (
                  <DestinationForm
                    key={d.id}
                    user={user}
                    customerId={rec.id}
                    current={d}
                    onCancel={() => setDestForm(null)}
                    onSaved={(r) => {
                      setDestForm(null)
                      saved(r, `Saved ${d.name}`)
                    }}
                  />
                ) : (
                  <div key={d.id} className={styles.destCard} data-testid="destination-card">
                    <div className={styles.infoHead}>
                      <span className={d.archived ? styles.nameArchived : styles.destName}>{d.name}</span>
                      {d.archived && <span className={styles.chipArchived}>Archived</span>}
                      <span className={styles.destUse}>{d.orders ? `On ${plural(d.orders, 'order')}` : 'On no orders'}</span>
                      <div className={ps.dHeadRight}>
                        <button type="button" className={styles.rowBtn} disabled={busy} onClick={() => leaveForm(() => setDestForm(d.id))}>
                          Edit
                        </button>
                        <PendingButton
                          className={styles.rowBtn}
                          busy={busyOn === `archive:${d.id}`}
                          disabled={busy}
                          pending={d.archived ? 'Restoring…' : 'Archiving…'}
                          onClick={() => destAct(d, d.archived ? 'restore' : 'archive')}
                        >
                          {d.archived ? 'Restore' : 'Archive'}
                        </PendingButton>
                        {d.removable && (
                          <PendingButton
                            className={styles.removeBtn}
                            busy={busyOn === `remove:${d.id}`}
                            disabled={busy}
                            pending="Removing…"
                            title="Remove: no order names it"
                            aria-label={`Remove ${d.name}`}
                            onClick={() => destAct(d, 'remove')}
                          >
                            ×
                          </PendingButton>
                        )}
                      </div>
                    </div>
                    <div className={styles.destGrid}>
                      <span className={ps.factK}>Address</span>
                      <span className={styles.infoText}>{d.address || d.country ? [d.address, d.country].filter(Boolean).join(', ') : '—'}</span>
                      <span className={ps.factK}>Port</span>
                      <span>{dash(d.port)}</span>
                      <span className={ps.factK}>Importer / consignee</span>
                      <span className={styles.infoText}>{dash(d.importer_consignee)}</span>
                      <span className={ps.factK}>Delivery instructions</span>
                      <span className={styles.infoText}>{dash(d.delivery_instructions)}</span>
                    </div>
                  </div>
                )
              )}
              {destForm === 'new' ? (
                <DestinationForm
                  user={user}
                  customerId={rec.id}
                  onCancel={() => setDestForm(null)}
                  onSaved={(r) => {
                    setDestForm(null)
                    saved(r, 'Destination added')
                  }}
                />
              ) : (
                <div>
                  <button type="button" className={pa.ghostBtn} onClick={() => leaveForm(() => setDestForm('new'))}>
                    Add destination
                  </button>
                </div>
              )}
              <div className={ps.note}>
                An order selects exactly one destination. Orders show a destination’s current details: documents that keep the details saved at the
                time are not deployed yet.
              </div>
            </>
          )}

          {rec && tab === 'labels' && (
            <>
              <TextField
                key={`labels-${rec.id}`}
                user={user}
                rec={rec}
                field="label_requirement"
                label="Label requirement"
                empty="None stated."
                onSaved={(r) => saved(r, 'Label requirement saved')}
              />
              <div className={ps.section}>Templates that can match this customer (Dataphyll-configured)</div>
              <div className={styles.infoEmpty}>Label templates are not deployed yet, so no template can match this customer.</div>
            </>
          )}

          {rec && tab === 'notes' && (
            <>
              <TextField
                key={`notes-${rec.id}`}
                user={user}
                rec={rec}
                field="notes"
                label="Notes"
                empty="None"
                onSaved={(r) => saved(r, 'Notes saved')}
              />
              <div className={ps.section}>Orders</div>
              <div className={styles.listCard}>
                {rec.order_list.length === 0 && <div className={styles.emptyRow}>No orders yet.</div>}
                {rec.order_list.map((o) => {
                  const c = approvalChip(o.approval_status)
                  return (
                    <button key={o.id} type="button" className={styles.cuOrderRow} data-testid="customer-order" onClick={() => setOrderId(o.id)}>
                      <span className={ps.orderRef}>{o.order_reference}</span>
                      <span>{fmtDay(o.requested_dispatch_date)}</span>
                      <span>{o.destination_name || '—'}</span>
                      <span className={ps.right}>
                        {o.dispatched} / {o.ordered} boxes
                      </span>
                      <span>
                        <span className={ps.chip} style={{ background: c.bg, color: c.ink }}>
                          {c.label}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
      {drawer.confirm}

      {orderId && (
        <OrderModal
          user={user}
          orderId={orderId}
          onClose={() => setOrderId(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
          onNotice={(msg) => onChanged(msg)}
        />
      )}
    </>
  )
}
